import { AUTH_PAGES, displayNameSchema, newPasswordSchema, Roles, routes } from '@pmai/contracts';
import { Alert, Button, Spinner, TextField } from '@pmai/ui';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';
import { z } from 'zod';
import { useApiMutation, useApiQuery } from '../../shared/api/hooks';
import { useApplySession, useSession } from '../../shared/auth/session';
import { applyFieldErrors, bind, useZodForm } from '../../shared/forms/form';
import { useEnumLabel, useErrorText } from '../../shared/i18n';
import { AuthCard, BackToLogin, NewPasswordField, useTokenParam } from './auth-layout';

const newAccountSchema = z.object({ displayName: displayNameSchema, password: newPasswordSchema });

/**
 * FR-WS-002: an existing account accepts while signed in as the invited e-mail; a new person
 * creates the account here (the link proves the e-mail).
 */
export function AcceptInvitationPage() {
  const { t } = useTranslation();
  const token = useTokenParam();
  const errorText = useErrorText();
  const roleLabel = useEnumLabel();
  const location = useLocation();
  const navigate = useNavigate();
  const session = useSession();
  const applySession = useApplySession();
  const preview = useApiQuery(
    routes.invitations.preview,
    { params: { token: token ?? '' }, anonymousOk: true },
    { enabled: token !== null, retry: false },
  );
  const form = useZodForm(newAccountSchema, { defaultValues: { displayName: '', password: '' } });
  const accept = useApiMutation(routes.invitations.accept, {
    onSuccess: (me) => {
      applySession(me);
      void navigate('/', { replace: true });
    },
    onError: (error) => applyFieldErrors(form, error),
  });

  const title = t('auth.invitation.title');
  if (!token) {
    return (
      <AuthCard title={title} footer={<BackToLogin />}>
        <Alert tone="danger">{t('auth.missingToken')}</Alert>
      </AuthCard>
    );
  }
  if (preview.isPending || session.isPending) return <Spinner label={t('common.loading')} />;
  if (preview.isError) {
    return (
      <AuthCard title={title} footer={<BackToLogin />}>
        <Alert tone="danger">{errorText(preview.error)}</Alert>
      </AuthCard>
    );
  }

  const invitation = preview.data;
  const me = session.data;
  const intro = (
    <div className="mb-5 flex flex-col gap-1 text-sm">
      <p>
        {t('auth.invitation.text', {
          workspace: invitation.workspaceName,
          role: roleLabel(Roles.byCode(invitation.role)),
        })}
      </p>
      <p className="text-ink-muted">{t('auth.invitation.forEmail', { email: invitation.email })}</p>
    </div>
  );
  const acceptError = accept.error && <Alert tone="danger">{errorText(accept.error)}</Alert>;

  if (invitation.accountExists) {
    const loginHref = `${AUTH_PAGES.login}?next=${encodeURIComponent(location.pathname + location.search)}`;
    return (
      <AuthCard title={title}>
        {intro}
        <div className="flex flex-col gap-4">
          {acceptError}
          {!me ? (
            <>
              <Alert tone="info">{t('auth.invitation.loginFirst', { email: invitation.email })}</Alert>
              <Link to={loginHref} className="text-center font-medium text-brand-700 hover:underline">
                {t('auth.invitation.loginAction')}
              </Link>
            </>
          ) : me.user.email !== invitation.email ? (
            <Alert tone="warning">
              {t('auth.invitation.wrongUser', { current: me.user.email, email: invitation.email })}
            </Alert>
          ) : (
            <Button loading={accept.isPending} onClick={() => accept.mutate({ body: { token } })}>
              {t('auth.invitation.accept')}
            </Button>
          )}
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={title}>
      {intro}
      <form
        noValidate
        className="flex flex-col gap-4"
        onSubmit={form.handleSubmit((values) => accept.mutate({ body: { token, ...values } }))}
      >
        <p className="text-sm font-medium">{t('auth.invitation.newAccount')}</p>
        {acceptError}
        <TextField
          label={t('auth.register.displayName')}
          autoComplete="name"
          required
          {...bind(form, 'displayName')}
        />
        <NewPasswordField {...bind(form, 'password')} />
        <Button type="submit" loading={accept.isPending}>
          {t('auth.invitation.accept')}
        </Button>
      </form>
    </AuthCard>
  );
}
